// This is a helper reducer which can be used to provide the display config to the imported chart
// and handles chart local state + global state management in a sensible way

import produce from 'immer';
import { useEffect, useReducer } from 'react';
import { match } from 'ts-pattern';
import { JsonLocalStorageService } from '../../../../../../../api/local-storage-service';
import { reducerWithPersistentWrapper } from '../../../../../../../context/hooks';
import { GlobalDisplayState } from '../../../../../../../context/types';
import { SortTypes } from '../../../../../../types';
import {
  BarChartLayouts,
  ChartDisplayAction,
  ChartDisplayStateAndGlobalDisplayState,
  ChartDisplayStateUpdateOptions,
  GlobalStateSyncAction,
} from '../common-chart-types';

// The chart display field each global display setting controls.
const globalToChartDisplayField: Partial<
  Record<keyof GlobalDisplayState, keyof ChartDisplayStateAndGlobalDisplayState>
> = {
  showLabels: 'showLabels',
  showStack: 'layout',
  showPercentage: 'layout',
  showLegend: 'showLegend',
  showGridLines: 'showGridLines',
};

// Copies the global display settings selected by shouldApply onto the chart state
// and records globalDisplayState as the baseline for detecting later global changes.
const applyGlobalDisplayState = (
  state: ChartDisplayStateAndGlobalDisplayState,
  globalDisplayState: GlobalDisplayState,
  shouldApply: (key: keyof GlobalDisplayState) => boolean
): ChartDisplayStateAndGlobalDisplayState =>
  produce(state, (draft) => {
    if (shouldApply('showLabels')) {
      draft.showLabels = globalDisplayState.showLabels;
    }
    if (shouldApply('showStack') || shouldApply('showPercentage')) {
      if (globalDisplayState.showStack && draft.enableStack !== false) {
        draft.layout = BarChartLayouts.STACKED;
      } else if (
        globalDisplayState.showPercentage &&
        draft.enablePercentage !== false
      ) {
        draft.layout = BarChartLayouts.PERCENTAGE;
      } else {
        draft.layout = BarChartLayouts.GROUPED;
      }
    }
    if (shouldApply('showLegend')) {
      draft.showLegend = globalDisplayState.showLegend;
    }
    if (shouldApply('showGridLines')) {
      draft.showGridLines = globalDisplayState.showGridLines;
    }
    draft.globalDisplayState = globalDisplayState;
  });

const reducer = (
  state: ChartDisplayStateAndGlobalDisplayState,
  action: ChartDisplayAction
): ChartDisplayStateAndGlobalDisplayState => {
  const newState = match(action)
    .with({ type: 'toggle-labels' }, () => {
      return {
        ...state,
        showLabels: !state.showLabels,
      };
    })
    .with({ type: 'toggle-percentage' }, () => {
      const newChartLayout =
        state.layout === BarChartLayouts.PERCENTAGE
          ? BarChartLayouts.GROUPED
          : BarChartLayouts.PERCENTAGE;
      return {
        ...state,
        layout: newChartLayout,
      };
    })
    .with({ type: 'toggle-stack' }, () => {
      const newChartLayout =
        state.layout === BarChartLayouts.STACKED
          ? BarChartLayouts.GROUPED
          : BarChartLayouts.STACKED;
      return {
        ...state,
        layout: newChartLayout,
      };
    })
    .with({ type: 'toggle-legend' }, () => {
      return {
        ...state,
        showLegend: !state.showLegend,
      };
    })
    .with({ type: 'toggle-wrapped-x-axis-labels' }, () => {
      return {
        ...state,
        showWrappedXAxisLabels: !state.showWrappedXAxisLabels,
      };
    })
    .with(
      { type: 'toggle-sort-order' },
      (a: { type: 'toggle-sort-order'; selectedSortOrder: SortTypes }) => {
        return {
          ...state,
          selectedSortOrder: a.selectedSortOrder,
        };
      }
    )
    .with({ type: 'toggle-table-view' }, () => {
      return {
        ...state,
        showTableView: !state.showTableView,
      };
    })
    .with({ type: 'global-state-sync' }, (a) =>
      applyGlobalDisplayState(state, a.globalDisplayState, (key) => {
        return state.globalDisplayState[key] !== a.globalDisplayState[key];
      })
    )
    .exhaustive();
  return newState;
};

export const useChartDisplayReducer = (
  initialState: ChartDisplayStateAndGlobalDisplayState,
  persistenceKey: string
) => {
  const localStorageService =
    new JsonLocalStorageService<ChartDisplayStateAndGlobalDisplayState>(
      persistenceKey
    );
  const storedItems = localStorageService.get();

  // A chart mounted for the first time starts from the global display settings,
  // except for fields its initialState sets explicitly.
  // A chart with persisted state keeps its own choices, except for global settings
  // changed since it last saw them (e.g. toggled while on another storyboard).
  // Capability flags (enablePercentage, enableStack) always come from initialState
  // so that chart opt-ins for display features are not overridden by old persisted state.
  const mergedState = storedItems
    ? reducer(
        {
          ...storedItems,
          globalDisplayState:
            storedItems.globalDisplayState ?? initialState.globalDisplayState,
          enablePercentage: initialState.enablePercentage,
          enableStack: initialState.enableStack,
        },
        {
          type: 'global-state-sync',
          globalDisplayState: initialState.globalDisplayState,
        }
      )
    : applyGlobalDisplayState(
        initialState,
        initialState.globalDisplayState,
        (key) => {
          const chartField = globalToChartDisplayField[key];
          return (
            chartField !== undefined && initialState[chartField] === undefined
          );
        }
      );

  // Not using useReducerWithPersistence here because we need to merge the persisted state with the initial state
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    mergedState
  );
};

export const useSyncGlobalDisplayState = (
  displayGlobalState: GlobalDisplayState,
  displayReducer: React.Dispatch<GlobalStateSyncAction>
) => {
  useEffect(() => {
    const action: GlobalStateSyncAction = {
      type: 'global-state-sync',
      globalDisplayState: displayGlobalState,
    };
    displayReducer(action);
  }, [displayGlobalState]);
};

/**
 * Reusable hook that combines useChartDisplayReducer, useSyncGlobalDisplayState,
 * and display action handlers into a single interface.
 *
 * @param initialState - The initial state configuration for the chart display
 * @param persistenceKey - The key used for persisting the display state
 * @returns An object containing the display state and action handlers
 */
export const useChartDisplay = (
  initialState: ChartDisplayStateAndGlobalDisplayState,
  persistenceKey: string
) => {
  const [displayState, dispatch] = useChartDisplayReducer(
    initialState,
    persistenceKey
  );
  useSyncGlobalDisplayState(initialState.globalDisplayState, dispatch);

  const displayActions: ChartDisplayStateUpdateOptions = {
    onClickPercentage: () => dispatch({ type: 'toggle-percentage' }),
    onClickStack: () => dispatch({ type: 'toggle-stack' }),
    onClickLabels: () => dispatch({ type: 'toggle-labels' }),
    onClickLegend: () => dispatch({ type: 'toggle-legend' }),
    onClickWrappedXAxisLabels: () =>
      dispatch({ type: 'toggle-wrapped-x-axis-labels' }),
    onSelectSortOrder: (sortOrder) => {
      dispatch({
        type: 'toggle-sort-order',
        selectedSortOrder: sortOrder,
      });
    },
    onClickTableView: () => dispatch({ type: 'toggle-table-view' }),
  };

  return {
    displayState,
    displayActions,
  };
};
